import { GiftDedicationFields, EMPTY_GIFT, giftPayload } from "@/components/GiftDedicationFields";
import React, { useEffect, useMemo, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { toast } from "sonner";
import { PurchaseReceipt } from "@/components/PurchaseReceipt";
import { Gift, Search, CheckCircle2, BadgePercent, Copy, Flame, ShieldCheck } from "lucide-react";

const money = (v, cur) => (cur && cur !== "EUR" ? `${Number(v).toFixed(2)} ${cur}` : euro(v));

const COUNTRIES = [
  ["IT", "Italia"], ["GB", "Regno Unito"], ["US", "Stati Uniti"], ["FR", "Francia"],
  ["DE", "Germania"], ["ES", "Spagna"], ["PT", "Portogallo"], ["NL", "Paesi Bassi"],
  ["BE", "Belgio"], ["IE", "Irlanda"], ["AT", "Austria"], ["CH", "Svizzera"],
  ["PL", "Polonia"], ["SE", "Svezia"], ["DK", "Danimarca"], ["NO", "Norvegia"],
  ["FI", "Finlandia"], ["GR", "Grecia"], ["CZ", "Rep. Ceca"], ["RO", "Romania"],
  ["CA", "Canada"], ["AU", "Australia"], ["BR", "Brasile"], ["MX", "Messico"],
  ["IN", "India"], ["JP", "Giappone"], ["AE", "Emirati Arabi"], ["TR", "Turchia"],
  ["ZA", "Sudafrica"], ["SA", "Arabia Saudita"],
];

export default function GiftCardCatalog() {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ role: "dealer", default_master: 2, default_dealer: 1 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("IT");

  // order dialog state
  const [sel, setSel] = useState(null); // selected product
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [sender, setSender] = useState("Markoo Place");
  const [gift, setGift] = useState(EMPTY_GIFT);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const load = (c) => {
    const cc = ((c || country || "IT")).toUpperCase();
    setLoading(true); setErr("");
    api.get("/reloadly/giftcards/catalog", { params: { country: cc } })
      .then((r) => { setProducts(r.data.products || []); setMeta(r.data); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Catalogo non disponibile"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => products.filter((p) =>
    !search || (p.brandName || "").toLowerCase().includes(search.toLowerCase()) || (p.productName || "").toLowerCase().includes(search.toLowerCase())
  ), [products, search]);

  const openOrder = (p) => {
    setSel(p); setRes(null); setEmail(""); setSender("Markoo Place"); setGift(EMPTY_GIFT); setPendingConfirm(false);
    const fixed = p.fixedRecipientDenominations || [];
    setAmount(fixed.length ? String(fixed[0]) : (p.minRecipientDenomination != null ? String(p.minRecipientDenomination) : ""));
  };

  const amt = parseFloat(amount) || 0;
  const earning = sel ? +(amt * (sel.margin_percent || 0) / 100).toFixed(2) : 0;
  const debit = sel ? +(amt - earning).toFixed(2) : 0;

  const confirmOrder = async () => {
    if (!sel) return;
    if (!(amt > 0)) { toast.error("Seleziona o inserisci un importo valido"); return; }
    if (!email.trim()) { toast.error("Inserisci l'email del destinatario"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/reloadly/giftcards/order", {
        productId: sel.productId, unitPrice: amt, quantity: 1,
        recipientEmail: email.trim(), senderName: sender.trim() || "Markoo Place",
        brandName: sel.brandName || sel.productName, gift: giftPayload(gift),
      });
      setRes(data);
      toast.success("Gift card acquistata!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Ordine gift card fallito");
    } finally { setBusy(false); }
  };

  const askConfirm = () => {
    if (!(amt > 0)) { toast.error("Seleziona o inserisci un importo valido"); return; }
    if (!email.trim()) { toast.error("Inserisci l'email del destinatario"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { toast.error("Email non valida: controlla l'indirizzo per l'invio del PIN"); return; }
    if (gift.enabled && !gift.recipient_name.trim()) { toast.error("Inserisci il nome di chi riceve il regalo"); return; }
    setPendingConfirm(true);
  };

  const closeDialog = () => { setSel(null); setRes(null); setPendingConfirm(false); };

  return (
    <div className="space-y-6" data-testid="giftcard-catalog-page">
      <ServiceHistoryButton service="giftcard" />
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizi pubblici</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Gift className="w-7 h-7 text-primary" /> Catalogo Gift Card</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {meta.role === "admin" ? <>Vista amministratore: prezzi finali al cliente, margine dealer <strong className="text-teal-500">{meta.default_dealer}%</strong>, master <strong className="text-teal-500">{meta.default_master}%</strong>. Gli ordini li fanno dealer e master.</> : <>Vendi gift card di tutti i brand. Il prezzo mostrato è quello finale al cliente; il tuo margine è
          <strong className="text-teal-500"> {meta.role === "master" ? meta.default_master : meta.default_dealer}%</strong> (ruolo: {meta.role === "master" ? "Master" : "Dealer"}).</>}
        </p>
      </div>


      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cerca brand…" data-testid="giftcard-catalog-search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Paese</span>
          <Badge variant="outline" className="gap-1 h-9 px-3" data-testid="giftcard-country-it">Italia (IT)</Badge>
        </div>
      </div>

      {loading && <p className="text-muted-foreground" data-testid="giftcard-catalog-loading">Carico il catalogo…</p>}
      {err && !loading && (
        <Card className="p-6 glow-border" data-testid="giftcard-catalog-error">
          <p className="text-sm text-destructive">{err}</p>
          <Button variant="outline" className="mt-3" onClick={load} data-testid="giftcard-catalog-retry">Riprova</Button>
        </Card>
      )}

      {!loading && !err && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => {
            const fixed = p.fixedRecipientDenominations || [];
            const isRange = p.denominationType === "RANGE" || (!fixed.length && p.minRecipientDenomination != null);
            return (
              <Card key={p.productId} className="overflow-hidden glow-border flex flex-col" data-testid={`giftcard-brand-${p.productId}`}>
                <div className="h-36 bg-secondary/40 relative flex items-center justify-center p-4">
                  {p.logoUrl
                    ? <img src={p.logoUrl} alt={p.brandName} className="max-h-full max-w-full object-contain" />
                    : <Gift className="w-12 h-12 text-primary/50" />}
                  {p.popular && <Badge className="absolute top-3 left-3 gap-1 bg-amber-500 hover:bg-amber-500 text-white border-0" data-testid={`giftcard-popular-${p.productId}`}><Flame className="w-3 h-3" />Più venduti</Badge>}
                  <Badge className="absolute top-3 right-3 gap-1 bg-teal-600 hover:bg-teal-600"><BadgePercent className="w-3 h-3" />{p.margin_percent}%</Badge>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-head font-semibold text-base leading-tight">{p.brandName || p.productName}</h3>
                  <p className="text-xs text-muted-foreground mt-1 flex-1">
                    {isRange
                      ? `Importo libero ${money(p.minRecipientDenomination, p.recipientCurrencyCode)} – ${money(p.maxRecipientDenomination, p.recipientCurrencyCode)}`
                      : `Tagli: ${fixed.slice(0, 6).map((d) => money(d, p.recipientCurrencyCode)).join(", ")}${fixed.length > 6 ? "…" : ""}`}
                  </p>
                  <Button size="sm" style={{ "--action-button-width": "50%" }} className="mt-4 self-center rounded-full px-3 py-1.5 gap-1" onClick={() => openOrder(p)} data-testid={`giftcard-sell-${p.productId}`}>
                    <Gift className="w-4 h-4" /> Vendi
                  </Button>
                </div>
              </Card>
            );
          })}
          {filtered.length === 0 && <p className="text-muted-foreground">Nessun brand trovato.</p>}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-3xl" data-testid="giftcard-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> {sel?.brandName || sel?.productName}</DialogTitle>
            <DialogDescription>{res ? "Gift card generata con successo." : "Scegli l'importo e inserisci i dati del cliente."}</DialogDescription>
          </DialogHeader>

          {!res && sel && (
            <div className="space-y-3">
              {(sel.fixedRecipientDenominations || []).length > 0 ? (
                <div className="flex flex-wrap gap-2" data-testid="giftcard-denoms">
                  {sel.fixedRecipientDenominations.map((d) => (
                    <Button key={d} type="button" size="sm" className="sim-chip" variant={String(d) === amount ? "default" : "outline"}
                      onClick={() => setAmount(String(d))} data-testid={`giftcard-denom-${d}`}>{money(d, sel.recipientCurrencyCode)}</Button>
                  ))}
                </div>
              ) : (
                <div>
                  <Label>Importo ({sel.recipientCurrencyCode || "EUR"})</Label>
                  <Input type="number" step="0.01" className="mt-1.5" data-testid="giftcard-amount-input"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder={`min ${sel.minRecipientDenomination} · max ${sel.maxRecipientDenomination}`} />
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Email destinatario *</Label>
                <Input type="email" className="mt-1" data-testid="giftcard-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.it" />
              </div>
              <div>
                <Label>Mittente</Label>
                <Input className="mt-1" data-testid="giftcard-sender-input" value={sender} onChange={(e) => setSender(e.target.value)} />
              </div>
              </div>
              <GiftDedicationFields gift={gift} setGift={setGift} sender={sender} brand={sel.brandName || sel.productName} />

              <div className="rounded-lg border border-border p-3 text-sm space-y-1" data-testid="giftcard-order-summary">
                <div className="flex justify-between"><span className="text-muted-foreground">Prezzo di vendita</span><span className="font-medium">{money(amt, sel.recipientCurrencyCode)}</span></div>
                <div className="flex justify-between text-teal-500"><span>Tuo margine ({sel.margin_percent}%)</span><span className="font-medium" data-testid="giftcard-earning">+ {euro(earning)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="text-muted-foreground">Addebito wallet</span><span className="font-head font-bold" data-testid="giftcard-debit">{euro(debit)}</span></div>
              </div>

              {!pendingConfirm ? (
                <DialogFooter>
                  <Button variant="outline" onClick={closeDialog} data-testid="giftcard-order-cancel">Annulla</Button>
                  <Button onClick={askConfirm} disabled={busy} className="gap-1" data-testid="giftcard-order-confirm">
                    <Gift className="w-4 h-4" />Acquista · {euro(debit)}
                  </Button>
                </DialogFooter>
              ) : (
                <div className="rounded-lg border-2 border-amber-500/50 bg-amber-500/5 p-3 space-y-3" data-testid="giftcard-confirm-step">
                  <p className="text-sm font-medium flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-amber-500" /> Conferma l'acquisto</p>
                  <p className="text-sm text-muted-foreground">Il PIN della gift card <strong className="text-foreground">{sel.brandName || sel.productName}</strong> da <strong className="text-foreground">{money(amt, sel.recipientCurrencyCode)}</strong> verrà inviato via email a:</p>
                  <p className="text-base font-head font-bold break-all" data-testid="giftcard-confirm-email">{email.trim()}</p>
                  <p className="text-xs text-muted-foreground">Addebito wallet: <strong className="text-foreground">{euro(debit)}</strong>. Verifica che l'indirizzo sia corretto: il codice non è recuperabile se inviato all'email sbagliata.</p>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setPendingConfirm(false)} disabled={busy} data-testid="giftcard-confirm-back">Modifica</Button>
                    <Button onClick={confirmOrder} disabled={busy} className="gap-1" data-testid="giftcard-confirm-final"><Gift className="w-4 h-4" />{busy ? "Acquisto…" : "Conferma e invia PIN"}</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {res && (
            <div className="space-y-3 text-center py-2" data-testid="giftcard-order-result">
              <CheckCircle2 className="w-12 h-12 text-teal-500 mx-auto" />
              <p className="text-sm">Transazione: <span className="font-mono">{res.transactionId}</span></p>
              <div className="rounded-lg border border-border p-3 text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Prezzo di vendita</span><span>{euro(res.sale_price)}</span></div>
                <div className="flex justify-between text-teal-500"><span>Margine ({res.margin_percent}%)</span><span>+ {euro(res.earning)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Addebito wallet</span><span className="font-medium">{euro(res.debit)}</span></div>
              </div>
              <p className="text-xs text-muted-foreground">Il codice è stato inviato via email al cliente.</p>
              <div className="flex justify-center" data-testid="giftcard-receipt-actions"><PurchaseReceipt refId={String(res.transactionId)} /></div>
              <DialogFooter>
                <Button variant="outline" onClick={closeDialog} data-testid="giftcard-order-close">Chiudi</Button>
                <Button onClick={() => openOrder(sel)} data-testid="giftcard-order-again">Nuova vendita</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
